// Starts the app in the browser: reads config.json, then renders the app
// (start-app.tsx shows a plain message with Try again if it cannot).
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { createApiClient } from './app/api-client';
import { ApiProvider } from './app/api-context';
import { CallerProvider } from './app/caller-context';
import { AppShell } from './app/app-shell';
import { AppRoutes } from './app/routes';
import { startApp, type AppConfig } from './app/start-app';
import { AppErrorBoundary } from './app/error-boundary';
import { logClientError } from './app/client-log';
import { ZoomSwitchProvider } from './meeting-choice/zoom-switch';
import { WebinarChangesSwitchProvider } from './offering-form/webinar-changes-switch';
import { RegistrationsSwitchProvider } from './app/registrations-switch';
import { LearnerEmailsSwitchProvider } from './calendar/learner-emails-switch';
import './styles.css';

const reload = () => window.location.reload();

function renderApp(root: HTMLElement, config: AppConfig): void {
  const client = createApiClient({ apiBase: config.apiBase });
  createRoot(root).render(
    <StrictMode>
      <AppErrorBoundary reload={reload} log={logClientError}>
        <ApiProvider
          client={client}
          navigateAway={(url) => window.location.assign(url)}
          random={Math.random}
        >
          <CallerProvider>
            <ZoomSwitchProvider>
              <WebinarChangesSwitchProvider>
                <RegistrationsSwitchProvider>
                  <LearnerEmailsSwitchProvider>
                    <BrowserRouter>
                      <AppShell>
                        <AppRoutes />
                      </AppShell>
                    </BrowserRouter>
                  </LearnerEmailsSwitchProvider>
                </RegistrationsSwitchProvider>
              </WebinarChangesSwitchProvider>
            </ZoomSwitchProvider>
          </CallerProvider>
        </ApiProvider>
      </AppErrorBoundary>
    </StrictMode>,
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('The page has no #root element');

void startApp({
  root,
  loadConfig: async (): Promise<unknown> =>
    (await fetch('/config.json', { cache: 'no-store' })).json(),
  renderApp,
  reload,
  log: logClientError,
});
